import React, { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import api, { formatApiError } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { EQUIPMENT_TYPES } from "@/lib/status";
import ResetPasswordDialog from "@/components/app/ResetPasswordDialog";
import PmocHistoryDialog from "@/components/app/PmocHistoryDialog";
import QrCodeDialog from "@/components/app/QrCodeDialog";
import NewRequestDialog from "@/components/app/NewRequestDialog";
import EquipmentMediaFields from "@/components/app/EquipmentMediaFields";
import QRCode from "qrcode";
import { toast } from "sonner";
import {
  Building2, Plus, Pencil, KeyRound, Wind, Trash2, MapPin, ShieldCheck, Package, ClipboardCheck, QrCode, Printer, ClipboardPlus, History, CalendarDays, FileText, Upload,
} from "lucide-react";

function ContractDocuments({ contract }) {
  const [docs, setDocs] = useState(contract?.documents || []);
  const [busy, setBusy] = useState(false);
  const [docType, setDocType] = useState("Contrato completo");

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/contracts/${contract.id}/documents`, fd, { params: { doc_type: docType } });
      setDocs((d) => [...d, data]);
      toast.success("Documento enviado.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Falha no upload");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/contracts/${contract.id}/documents/${id}`);
      setDocs((d) => d.filter((x) => x.id !== id));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid="contract-documents">
      <p className="text-xs font-semibold text-slate-600">Documentos importantes (PDF)</p>
      <div className="flex gap-2 items-center">
        <select value={docType} onChange={(e) => setDocType(e.target.value)}
          className="text-sm border border-slate-200 rounded-md px-2 py-1.5 flex-1" data-testid="contract-doc-type">
          <option>Contrato completo</option>
          <option>Termos e condições</option>
          <option>Política de privacidade</option>
          <option>Outro</option>
        </select>
        <label className="cursor-pointer inline-flex items-center gap-1.5 rounded-md bg-blue-600 text-white text-sm px-3 py-1.5 hover:bg-blue-700" data-testid="contract-doc-upload-label">
          <Upload className="h-4 w-4" /> {busy ? "..." : "Enviar"}
          <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={upload} disabled={busy} data-testid="contract-doc-upload-input" />
        </label>
      </div>
      {docs.length > 0 && (
        <div className="space-y-1">
          {docs.map((d) => (
            <div key={d.id} className="flex items-center gap-2 text-sm" data-testid={`contract-doc-${d.id}`}>
              <FileText className="h-4 w-4 text-red-500 flex-shrink-0" />
              <span className="flex-1 min-w-0 truncate text-slate-700">{d.type} — {d.name}</span>
              <button type="button" onClick={() => remove(d.id)} className="text-red-500" data-testid={`contract-doc-del-${d.id}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
import { STATUS_META } from "@/lib/status";

function LocationHistory({ contractId, open }) {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open || !contractId) return;
    setLoading(true);
    api.get(`/contracts/${contractId}/history-by-location`)
      .then(({ data }) => setLocations(data.locations || []))
      .catch(() => setLocations([]))
      .finally(() => setLoading(false));
  }, [open, contractId]);

  if (loading) return <p className="text-slate-400 text-sm py-6 text-center">Carregando...</p>;
  if (!locations.length) return <p className="text-slate-500 py-8 text-center" data-testid="empty-locations">Nenhum equipamento ou atendimento por local ainda.</p>;

  return (
    <div className="space-y-4" data-testid="location-history-list">
      {locations.map((g, gi) => (
        <div key={gi} className="rounded-xl border border-slate-200 overflow-hidden" data-testid={`location-group-${gi}`}>
          <div className="bg-slate-50 px-4 py-2.5 flex items-center gap-2 border-b border-slate-200">
            <MapPin className="h-4 w-4 text-[#1e7be0]" />
            <span className="font-semibold text-slate-800 text-sm">{g.location}</span>
            <span className="text-xs text-slate-400 ml-auto">{g.equipment.length} equip. · {g.requests.length} atend.</span>
          </div>
          <div className="p-3 space-y-3">
            {g.equipment.length > 0 && (
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5 flex items-center gap-1"><Wind className="h-3 w-3" /> Equipamentos</p>
                <div className="space-y-1">
                  {g.equipment.map((eq) => (
                    <p key={eq.id} className="text-sm text-slate-700">
                      {[eq.equipment_type, eq.brand, eq.model].filter(Boolean).join(" ")}{eq.capacity_btu ? ` · ${eq.capacity_btu} BTU` : ""}
                    </p>
                  ))}
                </div>
              </div>
            )}
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5 flex items-center gap-1"><History className="h-3 w-3" /> Atendimentos</p>
              {g.requests.length === 0 ? (
                <p className="text-xs text-slate-400">Nenhum atendimento registrado neste local.</p>
              ) : (
                <div className="space-y-1.5">
                  {g.requests.map((r) => (
                    <div key={r.id} className="flex items-center gap-2 text-sm" data-testid={`location-request-${r.id}`}>
                      <span className={`text-[10px] font-medium px-2 py-0.5 rounded border ${(STATUS_META[r.status] || {}).color || "bg-slate-100 text-slate-600"}`}>
                        {(STATUS_META[r.status] || {}).label || r.status}
                      </span>
                      <span className="text-slate-700 flex-1 min-w-0 truncate">{r.service_type}{r.equipment_label || r.equipment ? ` · ${r.equipment_label || r.equipment}` : ""}</span>
                      {r.scheduled_date && <span className="text-xs text-slate-400 flex items-center gap-1"><CalendarDays className="h-3 w-3" />{r.scheduled_date}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

async function printLabelsBatch(equipmentList) {
  const origin = window.location.origin;
  const logo = `${origin}/logo-clima-branca-icon.png`;
  const esc = (s) => String(s == null ? "" : s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
  const cards = await Promise.all(equipmentList.map(async (eq) => {
    const url = `${origin}/eq/${eq.qr_token}`;
    const dataUrl = await QRCode.toDataURL(url, { width: 220, margin: 1 });
    const type = eq.equipment_type || "Equipamento";
    const model = [eq.brand, eq.model].filter(Boolean).join(" ");
    const local = eq.location || "";
    const company = eq.contract_company || "";
    return `<div class="label">
      <div class="head"><img src="${logo}"/><div><b>Clima Branca</b><span>Franquias</span></div></div>
      <div class="body">
        <h1>${esc(type)}</h1>
        ${model ? `<div class="model">${esc(model)}</div>` : ""}
        ${local ? `<div class="meta"><b>Local:</b> ${esc(local)}</div>` : ""}
        ${company ? `<div class="meta">${esc(company)}</div>` : ""}
        <div class="qr"><img src="${dataUrl}"/></div>
        <div class="cta">Aponte a câmera para abrir um chamado</div>
      </div>
    </div>`;
  }));
  const win = window.open("", "_blank", "width=900,height=700");
  if (!win) { toast.error("Permita pop-ups para imprimir as etiquetas."); return; }
  win.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Etiquetas QR</title>
    <style>
      *{box-sizing:border-box;margin:0;padding:0;font-family:Arial,Helvetica,sans-serif}
      body{padding:12px}
      .grid{display:grid;grid-template-columns:1fr 1fr;gap:12px}
      .label{border:2px solid #0a2a6c;border-radius:12px;overflow:hidden;page-break-inside:avoid}
      .head{background:linear-gradient(135deg,#0a2a6c,#1e7be0);color:#fff;padding:8px 10px;display:flex;align-items:center;gap:8px}
      .head img{height:28px;width:28px;border-radius:6px;background:#fff}
      .head b{font-size:13px;display:block;line-height:1.1}
      .head span{font-size:8px;letter-spacing:2px;text-transform:uppercase;opacity:.85}
      .body{padding:10px;text-align:center}
      .body h1{font-size:15px;color:#0a2a6c}
      .body .model{font-size:11px;color:#475569;margin-bottom:6px}
      .meta{font-size:11px;color:#334155}
      .qr{margin:8px auto 4px;display:inline-block;padding:6px;border:1px solid #e2e8f0;border-radius:8px}
      .qr img{width:170px;height:170px;display:block}
      .cta{font-size:10px;color:#1e7be0;font-weight:bold}
      @media print{.grid{gap:10px}}
    </style></head>
    <body onload="setTimeout(function(){window.print();},350)"><div class="grid">${cards.join("")}</div></body></html>`);
  win.document.close();
}

const EMPTY_CONTRACT = {
  name: "", company_name: "", email: "", phone: "", password: "", document: "",
  cep: "", street: "", number: "", neighborhood: "", city: "", state: "", franchisee_id: "auto",
  contract_number: "", signed_date: "", valid_from: "", valid_to: "", contract_type: "", plan: "", payment_method: "",
};

const CONTRACT_META = ["contract_number", "signed_date", "valid_from", "valid_to", "contract_type", "plan", "payment_method"];

function ContractDialog({ trigger, contract, onSaved, isManager = false, franchisees = [] }) {
  const isEdit = !!contract;
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_CONTRACT);
  const [busy, setBusy] = useState(false);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const init = () => {
    if (isEdit) {
      const a = contract.address || {};
      setForm({
        name: contract.name || "", company_name: contract.company_name || "",
        email: contract.email || "", phone: contract.phone || "", password: "",
        document: contract.document || "", cep: a.cep || "", street: a.street || "",
        number: a.number || "", neighborhood: a.neighborhood || "", city: a.city || "", state: a.state || "",
        franchisee_id: contract.franchisee_id || "auto",
        contract_number: contract.contract_number || "", signed_date: contract.signed_date || "",
        valid_from: contract.valid_from || "", valid_to: contract.valid_to || "",
        contract_type: contract.contract_type || "", plan: contract.plan || "", payment_method: contract.payment_method || "",
      });
    } else setForm(EMPTY_CONTRACT);
  };

  const lookupCep = async (cep) => {
    const d = (cep || "").replace(/\D/g, "");
    if (d.length !== 8) return;
    try {
      const res = await fetch(`https://viacep.com.br/ws/${d}/json/`);
      const data = await res.json();
      if (!data.erro) setForm((f) => ({ ...f, street: data.logradouro || f.street, neighborhood: data.bairro || f.neighborhood, city: data.localidade || f.city, state: data.uf || f.state }));
    } catch { /* silencioso */ }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const address = { cep: form.cep, street: form.street, number: form.number, neighborhood: form.neighborhood, city: form.city, state: form.state };
    const franchiseeId = form.franchisee_id === "auto" ? "" : form.franchisee_id;
    const meta = Object.fromEntries(CONTRACT_META.map((k) => [k, form[k] || null]));
    try {
      if (isEdit) {
        await api.patch(`/contracts/${contract.id}`, {
          name: form.name, company_name: form.company_name, email: form.email,
          phone: form.phone, document: form.document, address, ...meta,
          ...(isManager ? { franchisee_id: franchiseeId } : {}),
        });
        toast.success("Contrato atualizado!");
      } else {
        await api.post("/contracts", {
          name: form.name, company_name: form.company_name, email: form.email,
          phone: form.phone, password: form.password, document: form.document, address, ...meta,
          ...(isManager ? { franchisee_id: franchiseeId || null } : {}),
        });
        toast.success("Cliente de contrato criado!");
      }
      setOpen(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) init(); }}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="contract-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Editar cliente de contrato" : "Novo cliente de contrato"}</DialogTitle>
          <DialogDescription>Dados de acesso e endereço (define a região do franqueado).</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5"><Label>Empresa / Condomínio</Label>
            <Input required value={form.company_name} onChange={upd("company_name")} data-testid="contract-company-input" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5"><Label>Responsável</Label>
              <Input required value={form.name} onChange={upd("name")} data-testid="contract-name-input" /></div>
            <div className="space-y-1.5"><Label>Documento (CNPJ/CPF)</Label>
              <Input value={form.document} onChange={upd("document")} data-testid="contract-document-input" /></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5"><Label>E-mail (login)</Label>
              <Input type="email" required value={form.email} onChange={upd("email")} data-testid="contract-email-input" /></div>
            <div className="space-y-1.5"><Label>Telefone</Label>
              <Input required value={form.phone} onChange={upd("phone")} placeholder="(21) 99999-9999" data-testid="contract-phone-input" /></div>
          </div>
          {!isEdit && (
            <div className="space-y-1.5"><Label>Senha de acesso</Label>
              <Input type="password" required value={form.password} onChange={upd("password")} placeholder="Mínimo 6 caracteres" data-testid="contract-password-input" /></div>
          )}
          {isManager && (
            <div className="space-y-1.5"><Label>Franqueado responsável</Label>
              <Select value={form.franchisee_id} onValueChange={(v) => setForm({ ...form, franchisee_id: v })}>
                <SelectTrigger data-testid="contract-franchisee-select"><SelectValue placeholder="Automático (por região)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto" data-testid="contract-franchisee-auto">Automático (por região)</SelectItem>
                  {franchisees.map((f) => (
                    <SelectItem key={f.id} value={f.id} data-testid={`contract-franchisee-${f.id}`}>{f.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="rounded-lg bg-slate-50 p-3 space-y-2">
            <p className="text-xs font-semibold text-slate-600">Endereço</p>
            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-1.5"><Label className="text-xs">CEP</Label>
                <Input value={form.cep} onChange={upd("cep")} onBlur={(e) => lookupCep(e.target.value)} placeholder="00000-000" data-testid="contract-cep-input" /></div>
              <div className="space-y-1.5 col-span-2"><Label className="text-xs">Rua</Label>
                <Input value={form.street} onChange={upd("street")} data-testid="contract-street-input" /></div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-1.5"><Label className="text-xs">Número</Label>
                <Input value={form.number} onChange={upd("number")} data-testid="contract-number-input" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Bairro</Label>
                <Input value={form.neighborhood} onChange={upd("neighborhood")} /></div>
              <div className="grid grid-cols-2 gap-1">
                <div className="space-y-1.5"><Label className="text-xs">Cidade</Label>
                  <Input value={form.city} onChange={upd("city")} /></div>
                <div className="space-y-1.5"><Label className="text-xs">UF</Label>
                  <Input value={form.state} onChange={upd("state")} maxLength={2} /></div>
              </div>
            </div>
          </div>
          <div className="rounded-lg bg-blue-50/50 border border-blue-100 p-3 space-y-2">
            <p className="text-xs font-semibold text-[#0a2a6c]">Dados do contrato (aparecem para o cliente)</p>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5"><Label className="text-xs">Nº do contrato</Label>
                <Input value={form.contract_number} onChange={upd("contract_number")} placeholder="CB-2024-0587" data-testid="contract-num-input" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Data de assinatura</Label>
                <Input type="date" value={form.signed_date} onChange={upd("signed_date")} data-testid="contract-signed-input" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Vigência início</Label>
                <Input type="date" value={form.valid_from} onChange={upd("valid_from")} data-testid="contract-validfrom-input" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Vigência fim</Label>
                <Input type="date" value={form.valid_to} onChange={upd("valid_to")} data-testid="contract-validto-input" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Tipo de contrato</Label>
                <Input value={form.contract_type} onChange={upd("contract_type")} placeholder="Prestação de Serviços" data-testid="contract-type-input" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Plano contratado</Label>
                <Input value={form.plan} onChange={upd("plan")} placeholder="Manutenção Preventiva" data-testid="contract-plan-input" /></div>
              <div className="space-y-1.5 col-span-2"><Label className="text-xs">Forma de pagamento</Label>
                <Input value={form.payment_method} onChange={upd("payment_method")} placeholder="Mensal · Boleto" data-testid="contract-payment-input" /></div>
            </div>
          </div>

          {isEdit && <ContractDocuments contract={contract} />}

          <DialogFooter>
            <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={busy} data-testid="submit-contract-button">
              {busy ? "Salvando..." : isEdit ? "Salvar alterações" : "Criar contrato"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const EMPTY_EQ = {
  equipment_type: "Split Hi-Wall", brand: "", model: "", serial_number: "", location: "",
  capacity_btu: "", voltage: "", install_date: "", last_maintenance_date: "",
  warranty_type: "none", warranty_start: "", warranty_end: "", warranty_coverage: "",
  pmoc_public: false, qr_code: "", photo: "",
};

function EquipmentDialog({ contractId, trigger, equipment, onSaved }) {
  const isEdit = !!equipment;
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_EQ);
  const [busy, setBusy] = useState(false);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const init = () => {
    if (isEdit) {
      const w = equipment.warranty || {};
      setForm({
        equipment_type: equipment.equipment_type || "Split Hi-Wall", brand: equipment.brand || "",
        model: equipment.model || "", serial_number: equipment.serial_number || "",
        location: equipment.location || "", capacity_btu: equipment.capacity_btu || "", voltage: equipment.voltage || "",
        install_date: equipment.install_date || "", last_maintenance_date: equipment.last_maintenance_date || "",
        warranty_type: w.type || "none", warranty_start: w.start || "", warranty_end: w.end || "",
        warranty_coverage: w.coverage || "",
        pmoc_public: !!equipment.pmoc_public, qr_code: equipment.patrimonio || "", photo: equipment.photo || "",
      });
    } else setForm(EMPTY_EQ);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.photo) { toast.error("Envie a foto do equipamento."); return; }
    setBusy(true);
    const payload = {
      equipment_type: form.equipment_type, brand: form.brand, model: form.model,
      serial_number: form.serial_number, location: form.location, capacity_btu: form.capacity_btu, voltage: form.voltage,
      install_date: form.install_date || null, last_maintenance_date: form.last_maintenance_date || null,
      warranty: form.warranty_type === "none" ? null : {
        type: form.warranty_type, start: form.warranty_start || null,
        end: form.warranty_end || null, coverage: form.warranty_coverage || null,
      },
      pmoc_public: form.pmoc_public,
      ...(form.qr_code && !isEdit ? { qr_code: form.qr_code.trim() } : {}),
      ...(form.photo ? { photo: form.photo } : {}),
    };
    try {
      if (isEdit) {
        await api.patch(`/equipment/${equipment.id}`, payload);
        toast.success("Equipamento atualizado!");
      } else {
        await api.post(`/contracts/${contractId}/equipment`, payload);
        toast.success("Equipamento cadastrado!");
      }
      setOpen(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) init(); }}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="equipment-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Editar equipamento" : "Novo equipamento"}</DialogTitle>
          <DialogDescription>Dados do equipamento e da garantia.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5"><Label>Tipo</Label>
              <Select value={form.equipment_type} onValueChange={(v) => setForm({ ...form, equipment_type: v })}>
                <SelectTrigger data-testid="equipment-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {EQUIPMENT_TYPES.map((t) => <SelectItem key={t} value={t} data-testid={`equipment-type-${t}`}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Capacidade (BTU)</Label>
              <Input value={form.capacity_btu} onChange={upd("capacity_btu")} placeholder="12000" data-testid="equipment-btu-input" /></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5"><Label>Tensão</Label>
              <Input value={form.voltage} onChange={upd("voltage")} placeholder="Ex: 220V" data-testid="equipment-voltage-input" /></div>
            <div className="space-y-1.5"><Label>Marca</Label>
              <Input value={form.brand} onChange={upd("brand")} data-testid="equipment-brand-input" /></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5"><Label>Modelo</Label>
              <Input value={form.model} onChange={upd("model")} data-testid="equipment-model-input" /></div>
            <div className="space-y-1.5"><Label>Nº de série</Label>
              <Input value={form.serial_number} onChange={upd("serial_number")} data-testid="equipment-serial-input" /></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5"><Label>Localização interna</Label>
              <Input required value={form.location} onChange={upd("location")} placeholder="Sala 201" data-testid="equipment-location-input" /></div>
          </div>
          <EquipmentMediaFields qrCode={form.qr_code} onQr={(v) => setForm((f) => ({ ...f, qr_code: v }))}
            photo={form.photo} onPhoto={(p) => setForm((f) => ({ ...f, photo: p }))} qrDisabled={isEdit} />
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5"><Label>Data de instalação</Label>
              <Input type="date" value={form.install_date} onChange={upd("install_date")} data-testid="equipment-install-input" /></div>
            <div className="space-y-1.5"><Label>Última manutenção</Label>
              <Input type="date" value={form.last_maintenance_date} onChange={upd("last_maintenance_date")} data-testid="equipment-lastmaint-input" /></div>
          </div>
          <div className="rounded-lg bg-slate-50 p-3 space-y-2">
            <p className="text-xs font-semibold text-slate-600">Garantia</p>
            <div className="space-y-1.5"><Label className="text-xs">Tipo de garantia</Label>
              <Select value={form.warranty_type} onValueChange={(v) => setForm({ ...form, warranty_type: v })}>
                <SelectTrigger data-testid="warranty-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none" data-testid="warranty-none">Sem garantia</SelectItem>
                  <SelectItem value="fabrica" data-testid="warranty-fabrica">Fábrica</SelectItem>
                  <SelectItem value="servico" data-testid="warranty-servico">Serviço avulso</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.warranty_type !== "none" && (
              <>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1.5"><Label className="text-xs">Início</Label>
                    <Input type="date" value={form.warranty_start} onChange={upd("warranty_start")} data-testid="warranty-start-input" /></div>
                  <div className="space-y-1.5"><Label className="text-xs">Fim</Label>
                    <Input type="date" value={form.warranty_end} onChange={upd("warranty_end")} data-testid="warranty-end-input" /></div>
                </div>
                <div className="space-y-1.5"><Label className="text-xs">Cobertura</Label>
                  <Textarea rows={2} value={form.warranty_coverage} onChange={upd("warranty_coverage")} placeholder="Ex: compressor e peças" data-testid="warranty-coverage-input" /></div>
              </>
            )}
          </div>
          <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
            <div>
              <Label className="text-sm">Exibir PMOC no QR público</Label>
              <p className="text-xs text-slate-400">Autoriza mostrar o histórico de manutenção (PMOC) na página do QR Code.</p>
            </div>
            <Switch checked={form.pmoc_public} onCheckedChange={(v) => setForm({ ...form, pmoc_public: v })} data-testid="equipment-pmoc-public-switch" />
          </div>
          <DialogFooter>
            <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={busy} data-testid="submit-equipment-button">
              {busy ? "Salvando..." : isEdit ? "Salvar" : "Cadastrar equipamento"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EquipmentManagerDialog({ contract, open, onOpenChange }) {
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pmocEquip, setPmocEquip] = useState(null);
  const [qrEquip, setQrEquip] = useState(null);
  const [selected, setSelected] = useState([]);
  const [printing, setPrinting] = useState(false);
  const [view, setView] = useState("inventario");

  const load = useCallback(async () => {
    if (!contract) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/contracts/${contract.id}/equipment`);
      setEquipment(data);
      setSelected([]);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }, [contract]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const toggleAll = () => setSelected((s) => s.length === equipment.length ? [] : equipment.map((e) => e.id));

  const printSelected = async () => {
    const list = equipment.filter((e) => selected.includes(e.id));
    if (!list.length) { toast.error("Selecione ao menos um equipamento."); return; }
    setPrinting(true);
    try { await printLabelsBatch(list); }
    finally { setPrinting(false); }
  };

  const remove = async (eid) => {
    try {
      await api.delete(`/equipment/${eid}`);
      toast.success("Equipamento removido.");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="equipment-manager-dialog">
        <DialogHeader>
          <DialogTitle>Inventário — {contract?.company_name}</DialogTitle>
          <DialogDescription>Gerencie os equipamentos deste contrato.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-1 p-1 bg-slate-100 rounded-xl" data-testid="inventory-view-toggle">
          <button onClick={() => setView("inventario")} data-testid="view-inventario"
            className={`flex-1 text-sm font-medium py-2 rounded-lg transition-colors ${view === "inventario" ? "bg-white text-[#0a2a6c] shadow-sm" : "text-slate-500"}`}>
            Equipamentos
          </button>
          <button onClick={() => setView("salas")} data-testid="view-salas"
            className={`flex-1 text-sm font-medium py-2 rounded-lg transition-colors ${view === "salas" ? "bg-white text-[#0a2a6c] shadow-sm" : "text-slate-500"}`}>
            Histórico por Sala
          </button>
        </div>
        {view === "inventario" && (
        <>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          {equipment.length > 0 && (
            <div className="flex items-center gap-2">
              <Checkbox checked={selected.length === equipment.length && equipment.length > 0}
                onCheckedChange={toggleAll} data-testid="select-all-equipment" id="select-all" />
              <Label htmlFor="select-all" className="text-sm text-slate-600 cursor-pointer">Selecionar todos</Label>
              <Button size="sm" variant="outline" className="gap-1.5 ml-1" disabled={!selected.length || printing}
                onClick={printSelected} data-testid="print-selected-button">
                <Printer className="h-4 w-4" /> Imprimir etiquetas{selected.length ? ` (${selected.length})` : ""}
              </Button>
            </div>
          )}
          <EquipmentDialog contractId={contract?.id} onSaved={load}
            trigger={<Button size="sm" className="bg-blue-600 hover:bg-blue-700 gap-1.5" data-testid="add-equipment-button"><Plus className="h-4 w-4" /> Adicionar equipamento</Button>} />
        </div>
        {loading ? (
          <p className="text-slate-400 text-sm py-6 text-center">Carregando...</p>
        ) : equipment.length === 0 ? (
          <p className="text-slate-500 py-8 text-center" data-testid="empty-equipment">Nenhum equipamento cadastrado.</p>
        ) : (
          <div className="space-y-2" data-testid="equipment-list">
            {equipment.map((eq) => (
              <Card key={eq.id} className="border-slate-200" data-testid={`equipment-${eq.id}`}>
                <CardContent className="p-3 flex items-start gap-3">
                  <Checkbox className="mt-1 flex-shrink-0" checked={selected.includes(eq.id)}
                    onCheckedChange={() => toggle(eq.id)} data-testid={`select-equipment-${eq.id}`} />
                  <div className="h-9 w-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                    <Wind className="h-4 w-4 text-blue-500" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-slate-800 text-sm">
                      {eq.equipment_type}{eq.brand ? ` · ${eq.brand}` : ""}{eq.model ? ` ${eq.model}` : ""}
                    </p>
                    <p className="text-xs text-slate-500 flex items-center gap-1"><MapPin className="h-3 w-3" />{eq.location || "-"}{eq.capacity_btu ? ` · ${eq.capacity_btu} BTU` : ""}</p>
                    {eq.warranty?.type && (
                      <p className="text-xs text-green-700 flex items-center gap-1 mt-0.5">
                        <ShieldCheck className="h-3 w-3" /> Garantia {eq.warranty.type === "fabrica" ? "de fábrica" : "de serviço"}
                        {eq.warranty.end ? ` até ${eq.warranty.end}` : ""}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-600"
                      onClick={() => setQrEquip(eq)} data-testid={`qr-code-${eq.id}`} title="QR Code">
                      <QrCode className="h-4 w-4" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-[#1e7be0]"
                      onClick={() => setPmocEquip(eq)} data-testid={`pmoc-history-${eq.id}`} title="Histórico PMOC">
                      <ClipboardCheck className="h-4 w-4" />
                    </Button>
                    <EquipmentDialog equipment={eq} onSaved={load}
                      trigger={<Button size="icon" variant="ghost" className="h-8 w-8" data-testid={`edit-equipment-${eq.id}`}><Pencil className="h-4 w-4" /></Button>} />
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" data-testid={`delete-equipment-${eq.id}`}><Trash2 className="h-4 w-4" /></Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Remover equipamento?</AlertDialogTitle>
                          <AlertDialogDescription>Esta ação não pode ser desfeita.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Voltar</AlertDialogCancel>
                          <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => remove(eq.id)} data-testid={`delete-equipment-yes-${eq.id}`}>Remover</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        </>
        )}
        {view === "salas" && <LocationHistory contractId={contract?.id} open={open} />}
        <PmocHistoryDialog open={!!pmocEquip} onOpenChange={(o) => !o && setPmocEquip(null)}
          equipmentId={pmocEquip?.id}
          equipmentLabel={pmocEquip ? `${pmocEquip.equipment_type}${pmocEquip.brand ? ` · ${pmocEquip.brand}` : ""}${pmocEquip.location ? ` — ${pmocEquip.location}` : ""}` : ""} />
        <QrCodeDialog open={!!qrEquip} onOpenChange={(o) => !o && setQrEquip(null)}
          qrToken={qrEquip?.qr_token} equipment={qrEquip}
          equipmentLabel={qrEquip ? `${qrEquip.equipment_type}${qrEquip.brand ? ` · ${qrEquip.brand}` : ""}${qrEquip.location ? ` — ${qrEquip.location}` : ""}` : ""} />
      </DialogContent>
    </Dialog>
  );
}

export default function ContractsTab({ canCreate = false }) {
  const { user } = useAuth();
  const isManager = user?.role === "manager";
  const [contracts, setContracts] = useState([]);
  const [franchisees, setFranchisees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [managing, setManaging] = useState(null);
  const [resetResult, setResetResult] = useState(null);
  const [chamadoContract, setChamadoContract] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/contracts");
      setContracts(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!isManager) return;
    api.get("/franchisees").then(({ data }) => setFranchisees(data)).catch(() => {});
  }, [isManager]);

  const resetPassword = async (c) => {
    try {
      const { data } = await api.post(`/contracts/${c.id}/reset-password`);
      setResetResult(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const removeContract = async (c) => {
    try {
      await api.delete(`/contracts/${c.id}`);
      toast.success("Cliente de contrato removido.");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div>
      {canCreate && (
        <div className="flex justify-end mb-4">
          <ContractDialog onSaved={load} isManager={isManager} franchisees={franchisees}
            trigger={<Button className="bg-blue-600 hover:bg-blue-700 gap-1.5" data-testid="add-contract-button"><Plus className="h-4 w-4" /> Novo cliente de contrato</Button>} />
        </div>
      )}
      {loading ? (
        <p className="text-slate-400 text-sm">Carregando...</p>
      ) : contracts.length === 0 ? (
        <p className="text-slate-500 py-10 text-center" data-testid="empty-contracts">Nenhum cliente de contrato cadastrado.</p>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2" data-testid="contracts-list">
          {contracts.map((c) => (
            <Card key={c.id} className="border-slate-200" data-testid={`contract-${c.id}`}>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-blue-50 flex items-center justify-center">
                    <Building2 className="h-5 w-5 text-blue-500" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-slate-800 truncate">{c.company_name}</p>
                    <p className="text-xs text-slate-500 truncate">{c.name} · {c.email}</p>
                    {c.franchisee_name && (
                      <p className="text-xs text-blue-700 flex items-center gap-1 mt-0.5 truncate" data-testid={`contract-franchisee-name-${c.id}`}>
                        <MapPin className="h-3 w-3 flex-shrink-0" /> {c.franchisee_name}
                      </p>
                    )}
                  </div>
                  <Badge variant="secondary" className="gap-1"><Package className="h-3 w-3" />{c.equipment_count ?? 0}</Badge>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button size="sm" className="flex-1 gap-1.5 bg-blue-600 hover:bg-blue-700"
                    onClick={() => setChamadoContract(c)} data-testid={`open-request-contract-${c.id}`}>
                    <ClipboardPlus className="h-4 w-4" /> Novo chamado
                  </Button>
                  <Button size="sm" variant="outline" className="gap-1.5"
                    onClick={() => setManaging(c)} data-testid={`manage-equipment-${c.id}`}>
                    <Wind className="h-4 w-4" /> Inventário
                  </Button>
                  {canCreate && (
                    <>
                      <ContractDialog contract={c} onSaved={load} isManager={isManager} franchisees={franchisees}
                        trigger={<Button size="sm" variant="outline" className="gap-1.5" data-testid={`edit-contract-${c.id}`}><Pencil className="h-4 w-4" /></Button>} />
                      <Button size="sm" variant="outline" className="gap-1.5 text-amber-700 border-amber-200 hover:bg-amber-50"
                        onClick={() => resetPassword(c)} data-testid={`reset-contract-${c.id}`}>
                        <KeyRound className="h-4 w-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="outline" className="gap-1.5 text-red-600 border-red-200 hover:bg-red-50" data-testid={`delete-contract-${c.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Remover cliente de contrato?</AlertDialogTitle>
                            <AlertDialogDescription>
                              {c.company_name} e todos os equipamentos cadastrados serão removidos. Esta ação não pode ser desfeita.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Voltar</AlertDialogCancel>
                            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => removeContract(c)} data-testid={`delete-contract-yes-${c.id}`}>Remover</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      <EquipmentManagerDialog contract={managing} open={!!managing} onOpenChange={(o) => !o && setManaging(null)} />
      <ResetPasswordDialog open={!!resetResult} onOpenChange={(o) => !o && setResetResult(null)} result={resetResult} />
      <NewRequestDialog open={!!chamadoContract} onOpenChange={(o) => !o && setChamadoContract(null)}
        managerMode initialContract={chamadoContract} onCreated={() => { setChamadoContract(null); load(); }} />
    </div>
  );
}
